import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { initSession, startTransfer, registerStatusCallback, registerActivityCallback, testConnection } from '@ibm-aspera/sdk';
import { BehaviorSubject, Observable, from, of, throwError, defer } from 'rxjs';
import { catchError, tap, map, switchMap, filter, takeWhile, retry, first, timeout } from 'rxjs/operators';
import { BaseService } from '@adgate/common/services/base-service';
import { RESTApplicationResponse } from '../types/rest-application-response.type';

@Injectable({
    providedIn: 'root'
})
export class AsperaService extends BaseService
{
    private isSessionInitialized = false;
    private activeTransfers$ = new BehaviorSubject<Map<string, TransferProgress>>(new Map());
    private initializationError$ = new BehaviorSubject<string | null>(null);
    private sessionStatus$ = new BehaviorSubject<SessionStatus>('not_initialized');

    private asperaHost: string | null = null;
    private asperaServiceUser: string | null = null;
    private asperaServiceToken: string | null = null;

    private readonly RECENT_THRESHOLD = 10000;

    private getAsperaCredentials()
    {
        let url = `${environment.baseServiceUrl}common/getAsperaCredentials`;
        return this.sendRequest(url, null, 'get');
    }

    public initSession(appId: string = 'my-angular-app'): Observable<AsperaResponse>
    {
        if (this.isSessionInitialized) {
            return of({success: true, data: null, message: 'Aspera session already initialized'});
        }

        return this.getAsperaCredentials().pipe(
            switchMap((r:RESTApplicationResponse) => {
                this.asperaHost = environment.asperaServiceUrl;
                this.asperaServiceUser = r.data.asperaServiceUser;
                this.asperaServiceToken = r.data.asperaServiceToken;

                try {
                    this.sessionStatus$.next('initializing');
                    this.registerCallbacks();
                    initSession({appId: appId, connectSettings: {useConnect: true}});
                } catch (error) {
                    return throwError(() => new Error(this.parseInitializationError(error)));
                }

                return this.sessionStatus$.pipe(
                    filter(status => status === 'connected'),
                    first(),
                    timeout(10000),
                    switchMap(() => {
                        return from(testConnection()).pipe(
                            retry({count: 3, delay: 2000}),
                            tap(() => {
                                console.log('Aspera connection verified');
                            }),
                            catchError(() => {
                                return throwError(() => new Error('Aspera connection verification failed'));
                            })
                        );
                    })
                );
            }),
            switchMap(() => {
                this.isSessionInitialized = true;
                this.sessionStatus$.next('initialized');
                this.initializationError$.next(null);
                return of({success: true, data: null, message: 'Aspera initialization successfull'});
            }),
            catchError((error: Error) => {
                this.isSessionInitialized = false;
                this.sessionStatus$.next('error');
                this.initializationError$.next(error.message);
                return throwError(() => error);
            })
        );

        // return defer(() => {
        //     try {
        //         this.sessionStatus$.next('initializing');
        //         this.registerCallbacks();
        //         initSession({appId: appId, connectSettings: {useConnect: true}});
        //     } catch (error) {
        //         return throwError(() => new Error(this.parseInitializationError(error)));
        //     }

        //     return this.sessionStatus$.pipe(
        //         filter(status => status === 'connected'),
        //         first(),
        //         timeout(10000),
        //         switchMap(() => {
        //             return from(testConnection()).pipe(
        //                 retry({count: 3, delay: 2000}),
        //                 tap(() => {
        //                     console.log('Aspera connection verified');
        //                 }),
        //                 catchError(() => {
        //                     return throwError(() => new Error('Aspera connection verification failed'));
        //                 })
        //             );
        //         })
        //     );
        // }).pipe(
        //     switchMap(() => {
        //         this.isSessionInitialized = true;
        //         this.sessionStatus$.next('initialized');
        //         this.initializationError$.next(null);
        //         return of({success: true, data: null, message: 'Aspera initialization successfull'});
        //     }),
        //     catchError((error: Error) => {
        //         this.isSessionInitialized = false;
        //         this.sessionStatus$.next('error');
        //         this.initializationError$.next(error.message);
        //         return throwError(() => error);
        //     })
        // );
    }

    private registerCallbacks(): void
    {
        // Register activity callback for transfer progress
        registerActivityCallback((data: any) => {
            if (data?.transfers) {
                const newMap = new Map<string, TransferProgress>();
                const now = Date.now();

                data.transfers.forEach((transfer: any) => {
                    const endTimeMs = transfer.end_time ? new Date(transfer.end_time).getTime() : undefined;
                    const progress: TransferProgress = {
                        percentage: Math.round((transfer.percentage || 0) * 100),
                        rateKbps: transfer.rate_kbps,
                        status: transfer.status,
                        uuid: transfer.uuid,
                        bytesTransferred: transfer.bytes_written,
                        bytesTotal: transfer.bytes_expected,
                        endTime: endTimeMs
                    };

                    const isTerminal = progress.status === 'completed' ||
                                      progress.status === 'failed' ||
                                      progress.status === 'cancelled';

                    if (!isTerminal) {
                        newMap.set(transfer.uuid, progress);
                    } else if (endTimeMs && (now - endTimeMs < this.RECENT_THRESHOLD)) {
                        newMap.set(transfer.uuid, progress);
                    }
                });

                this.activeTransfers$.next(newMap);
            }
        });

        // Register status callback for connection status
        registerStatusCallback((status: any) => {
            if (status === 'RUNNING') {
                this.sessionStatus$.next('connected');
            } else if (status === 'STOPPED' || status === 'OUTDATED') {
                this.sessionStatus$.next('disconnected');
                this.handleInitializationError('IBM Aspera Connect is not running or needs update');
            }
        });
    }

    private parseInitializationError(error: any): string
    {
        const errorMessage = error?.message || error?.toString() || 'Unknown error';

        if (errorMessage.includes('Connect') || errorMessage.includes('not installed')) {
            return 'IBM Aspera Connect is not installed. Please install it to use high-speed transfers.';
        }

        if (errorMessage.includes('version') || errorMessage.includes('outdated')) {
            return 'IBM Aspera Connect needs to be updated to the latest version.';
        }

        if (errorMessage.includes('permission') || errorMessage.includes('access')) {
            return 'Permission denied. Please check browser permissions for Aspera Connect.';
        }

        if (errorMessage.includes('callback')) {
            return 'Failed to register transfer callbacks.';
        }

        return `Failed to initialize Aspera session: ${errorMessage}`;
    }

    private handleInitializationError(error: string): void
    {
        this.initializationError$.next(error);
        this.isSessionInitialized = false;
    }

    public getInitializationError(): Observable<string | null>
    {
        return this.initializationError$.asObservable();
    }

    public getSessionStatus(): Observable<SessionStatus>
    {
        return this.sessionStatus$.asObservable();
    }

    public isInitialized(): boolean
    {
        return this.isSessionInitialized;
    }

    public upload(dataTransfer: any): Observable<TransferProgress>
    {
        if( !this.isSessionInitialized ) {
            return throwError(() => new Error('Aspera session was not properly initialized'));
        }

        if (!dataTransfer || dataTransfer.files.length === 0) {
            return throwError(() => new Error('No files selected for upload'));
        }

        const transferRequest = this.buildTransferRequest(dataTransfer.files, this.asperaHost!, this.asperaServiceUser!, this.asperaServiceToken!);

        return this.initSession().pipe(
            switchMap((r:AsperaResponse) => {
                return from(startTransfer(transferRequest, {allow_dialogs: false, dataTransfer: dataTransfer} as any));
            }),
            switchMap((response: any) => {
                const transferUuid = response.uuid || response.transfer_spec?.uuid;

                if (!transferUuid) {
                    return throwError(() => new Error('Transfer started but no UUID received'));
                }

                // Return progress stream filtered to this specific transfer
                return this.monitorProgress(transferUuid);
            }),
            catchError((error) => {
                const errorMessage = typeof error === 'string' ? error : this.parseTransferError(error, 'upload');
                return throwError(() => new Error(errorMessage));
            })
        );
    }

    private monitorProgress(transferUuid: string): Observable<any>
    {
        return this.activeTransfers$.pipe(
            map(transfers => transfers.get(transferUuid)),
            filter((progress): progress is TransferProgress => progress !== undefined),
            tap(progress => {
                // If transfer failed, throw an error
                if (progress.status === 'failed') {
                    throw new Error('Transfer failed. Please check credentials and server connection.');
                }
            }),
            takeWhile(progress => {
                const isTerminal = progress.status === 'completed' ||
                                    progress.status === 'failed' ||
                                    progress.status === 'cancelled';
                return !isTerminal;
            }, true)
        );
    }

    private buildTransferRequest(files: any[], asperaHost: string, asperaUser: string, asperaToken: string): any
    {
        return {
            direction: 'send',
            remote_host: asperaHost,
            remote_user: asperaUser,
            remote_password: asperaToken,
            ssh_port: 33001,
            authentication: 'password',
            destination_root: '/',
            target_rate_kbps: 100000,
            // resume: 'sparse_checksum',
            resume: 'none',
            create_dir: true,
            overwrite: 'always',
            paths: files.map(file => ({
                source: file.name  // Pass the File object, not the name
            }))
        };
    }

    private parseTransferError(error: any, operation: 'upload' | 'download'): string
    {
        const errorMessage = error?.message || error?.toString() || 'Unknown error';

        if (errorMessage.includes('authentication') || errorMessage.includes('token')) {
            return 'Authentication failed. Please check your transfer credentials.';
        }

        if (errorMessage.includes('network') || errorMessage.includes('connection')) {
            return 'Network error occurred during transfer. Please check your connection.';
        }

        if (errorMessage.includes('file') || errorMessage.includes('path')) {
            return `File error during ${operation}. Please check the file path and permissions.`;
        }

        if (errorMessage.includes('cancelled') || errorMessage.includes('aborted')) {
            return `Transfer ${operation} was cancelled.`;
        }

        return `Failed to ${operation} file: ${errorMessage}`;
    }

}

export interface TransferProgress
{
    percentage: number;
    rateKbps: number;
    status: string;
    uuid?: string;
    bytesTransferred?: number;
    bytesTotal?: number;
    endTime?: number;
}

export interface AsperaResponse
{
    success: boolean;
    message: string;
    data: any;
}

export type SessionStatus = 'not_initialized' | 'initializing' | 'initialized' | 'connected' | 'disconnected' | 'error';
